import React, { useState, useEffect, useCallback } from "react";
import { Sparkles, Loader2, Calendar, Filter, Trash2, Heart, Wand2, Camera, Dna, Search } from "lucide-react";
import { API_URL } from "../../constants";
import AIImageWithDisclaimer from "../AIImageWithDisclaimer";

const CATEGORY_META = {
  invitation: { label: "Invitations", icon: Wand2, color: "#DC283C" },
  decor:      { label: "House Decor",  icon: Heart, color: "#7C3AED" },
  creator:    { label: "Creator Studio", icon: Camera, color: "#FF9F1C" },
  party_dna:  { label: "Party DNA",    icon: Dna,   color: "#10B981" },
};
const DAY_FILTERS = [
  { v: "1", l: "24h" }, { v: "7", l: "7 days" }, { v: "30", l: "30 days" },
  { v: "90", l: "3 mo" }, { v: "all", l: "All" },
];

const AdminAIGalleryTab = ({ auth }) => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ invitation: 0, decor: 0, creator: 0, party_dna: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [days, setDays] = useState("30");
  const [phone, setPhone] = useState("");
  const [phoneApplied, setPhoneApplied] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const headers = () => ({
    "X-Admin-Auth": auth?.token || "",
    "Content-Type": "application/json",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ category, days, limit: "100" });
      if (phoneApplied) params.set("customer_phone", phoneApplied);
      const res = await fetch(`${API_URL}/api/admin/ai-creations?${params}`, {
        credentials: "include", headers: headers(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
      setCounts(data.category_counts || {});
      setTotal(data.total_in_window || 0);
    } catch (e) {
      setError(e.message || "Could not load gallery");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, days, phoneApplied]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Hide this entry from the gallery? (Storage object kept.)")) return;
    const res = await fetch(`${API_URL}/api/admin/ai-creations/${id}`, {
      method: "DELETE", credentials: "include", headers: headers(),
    });
    if (res.ok) {
      setItems(items.filter(i => i.id !== id));
      setSelected(null);
    } else {
      alert("Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-ai-gallery-tab">
      <div>
        <h2 className="text-2xl font-black text-[#1E1B4B] flex items-center gap-2">
          <Sparkles className="text-[#DC283C]" size={20} /> AI Image Gallery
        </h2>
        <p className="text-xs text-[#475569]">Every customer-facing AI image — filter by category & date for marketing reuse and content insights.</p>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="p-3 rounded-2xl bg-gradient-to-br from-[#1E1B4B] to-[#312E81] text-white">
          <p className="text-[10px] uppercase tracking-widest opacity-70">Total in window</p>
          <p className="text-2xl font-black">{total}</p>
        </div>
        {Object.entries(CATEGORY_META).map(([k, m]) => {
          const Icon = m.icon;
          return (
            <div key={k} className="p-3 rounded-2xl bg-white border border-gray-200">
              <p className="text-[10px] uppercase tracking-widest text-[#475569] flex items-center gap-1"><Icon size={10} style={{ color: m.color }} /> {m.label}</p>
              <p className="text-xl font-black" style={{ color: m.color }}>{counts[k] || 0}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 p-3 space-y-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#475569] mr-1"><Filter size={11} className="inline mr-0.5" /> Category:</span>
          <button onClick={() => setCategory("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${category === "all" ? "bg-[#1E1B4B] text-white" : "bg-gray-100 text-[#475569] hover:bg-gray-200"}`}>All</button>
          {Object.entries(CATEGORY_META).map(([k, m]) => {
            const Icon = m.icon;
            return (
              <button key={k} onClick={() => setCategory(k)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition inline-flex items-center gap-1 ${category === k ? "text-white shadow-md" : "bg-gray-100 text-[#475569] hover:bg-gray-200"}`}
                style={category === k ? { backgroundColor: m.color } : {}}>
                <Icon size={11} /> {m.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#475569] mr-1"><Calendar size={11} className="inline mr-0.5" /> When:</span>
          {DAY_FILTERS.map(d => (
            <button key={d.v} onClick={() => setDays(d.v)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${days === d.v ? "bg-[#DC283C] text-white" : "bg-gray-100 text-[#475569] hover:bg-gray-200"}`}>{d.l}</button>
          ))}
        </div>
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); setPhoneApplied(phone.trim()); }}>
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#475569]"><Search size={11} className="inline mr-0.5" /> Phone:</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Filter by customer phone (full match)"
            className="flex-1 max-w-md px-3 py-1.5 rounded-xl border border-gray-200 text-xs outline-none focus:border-[#DC283C]" />
          <button type="submit" className="px-3 py-1.5 rounded-xl bg-[#1E1B4B] text-white text-xs font-bold hover:opacity-90">Apply</button>
          {phoneApplied && (
            <button type="button" onClick={() => { setPhone(""); setPhoneApplied(""); }}
              className="px-2 py-1.5 rounded-xl bg-gray-100 text-[#475569] text-xs">×</button>
          )}
        </form>
      </div>

      {/* Loading / error / empty */}
      {loading && <div className="text-center py-12 text-[#475569]"><Loader2 className="animate-spin mx-auto mb-2" size={24} /> Loading gallery…</div>}
      {!loading && error && <div className="p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <Sparkles size={28} className="text-[#DC283C]/40 mx-auto mb-2" />
          <p className="text-sm font-bold text-[#1E1B4B]">No images match these filters</p>
        </div>
      )}

      {/* Grid */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {items.map(item => {
            const meta = CATEGORY_META[item.category] || CATEGORY_META.decor;
            const Icon = meta.icon;
            const thumb = item.thumbnail_b64
              ? `data:image/jpeg;base64,${item.thumbnail_b64}`
              : item.image_base64
                ? `data:image/png;base64,${item.image_base64}`
                : "";
            return (
              <button key={item.id} onClick={() => setSelected(item)}
                className="group relative rounded-xl overflow-hidden bg-gray-100 aspect-square hover:scale-[1.03] hover:shadow-lg transition-all">
                {thumb ? (
                  <>
                    <img src={thumb} alt={item.category} className="w-full h-full object-cover" loading="lazy" />
                    <div className="pointer-events-none absolute inset-x-0 bottom-5 px-1.5 py-0.5 text-[7px] font-medium text-white text-center leading-tight bg-black/55 backdrop-blur-[2px]" data-testid="ai-image-disclaimer">
                      AI reference image. T&amp;C apply.
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400"><Icon size={28} /></div>
                )}
                <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-black/80 to-transparent">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[8px] font-bold text-white" style={{ backgroundColor: meta.color }}>
                    <Icon size={8} /> {meta.label}
                  </span>
                  {item.customer_phone && (
                    <span className="block text-white text-[9px] mt-0.5 truncate font-mono">…{item.customer_phone.slice(-4)}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 space-y-3">
              {(selected.image_base64 || selected.thumbnail_b64) && (
                <AIImageWithDisclaimer src={`data:image/${selected.image_base64 ? "png" : "jpeg"};base64,${selected.image_base64 || selected.thumbnail_b64}`}
                  alt="" className="w-full max-h-[60vh] object-contain rounded-2xl bg-gray-100" />
              )}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="space-y-1">
                  <p className="text-[10px] uppercase font-bold tracking-widest" style={{ color: (CATEGORY_META[selected.category] || {}).color }}>
                    {(CATEGORY_META[selected.category] || {}).label}
                  </p>
                  <p className="text-xs text-[#475569]">{new Date(selected.created_at).toLocaleString()}</p>
                  {selected.customer_phone && <p className="text-xs text-[#475569] font-mono">📱 {selected.customer_phone}</p>}
                  {selected.is_paid && <span className="inline-block mt-1 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 text-[10px] font-bold">PAID</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleDelete(selected.id)}
                    className="px-3 py-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold inline-flex items-center gap-1.5">
                    <Trash2 size={12} /> Hide
                  </button>
                  <button onClick={() => setSelected(null)} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-xs font-bold text-[#1E1B4B]">Close</button>
                </div>
              </div>
              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <div className="pt-2 border-t border-gray-100 text-[11px] text-[#475569] space-y-0.5">
                  {Object.entries(selected.metadata).slice(0, 12).map(([k, v]) => (
                    <div key={k}><span className="font-bold capitalize">{k.replace(/_/g, " ")}:</span> {String(v).slice(0, 100)}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAIGalleryTab;
